import type { ReactNode } from 'react';
import type { ToolCallEvent } from '../stores/chatStore';

// ---------------------------------------------------------------------------
// Executors: side-effect tools that mutate app state (zustand stores, router).
// One executor per tool name; the chat hook fires them after user
// confirmation for side-effect tools or immediately for "safe" tools.
// ---------------------------------------------------------------------------

type ToolExecutor = (args: Record<string, unknown>) => void;

let registeredExecutors: Record<string, ToolExecutor> = {};

export function registerToolExecutors(executors: Record<string, ToolExecutor>): void {
  registeredExecutors = { ...registeredExecutors, ...executors };
}

export function executeToolCall(name: string, args: Record<string, unknown>): boolean {
  const executor = registeredExecutors[name];
  if (executor) {
    executor(args);
    return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Renderers: tools that render a custom inline component inside the chat
// transcript instead of (or in addition to) the default ToolCallDisplay
// summary. Used for things like ChoiceCard / InlineWorkflowResults.
//
// A renderer receives the live ToolCallEvent plus the standard chat
// callbacks. If `onAnswer` is invoked, the chat hook sends a new user
// message with the provided text, continuing the conversation.
// ---------------------------------------------------------------------------

export interface ToolRendererProps {
  toolCall: ToolCallEvent;
  /** Send a user-facing response (e.g. ChoiceCard pick). */
  onAnswer?: (toolCallId: string, answerText: string) => void;
  /** Standard confirmation hooks; rarely needed by custom renderers. */
  onConfirm?: (toolCallId: string) => void;
  onCancel?: (toolCallId: string) => void;
}

export type ToolRenderer = (props: ToolRendererProps) => ReactNode;

let registeredRenderers: Record<string, ToolRenderer> = {};

export function registerToolRenderers(renderers: Record<string, ToolRenderer>): void {
  registeredRenderers = { ...registeredRenderers, ...renderers };
}

export function lookupToolRenderer(name: string): ToolRenderer | null {
  return registeredRenderers[name] ?? null;
}

/** Test-only: clear the registries so unit tests start from a known state. */
export function __resetToolRegistriesForTesting(): void {
  registeredExecutors = {};
  registeredRenderers = {};
}
